import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, User, Clock, CheckCircle, PhoneCall, PlusCircle, X, Trash2 } from 'lucide-react';
import LeadProfile from '../leads/components/LeadProfile';
import AddLead from '../leads/AddLead';
import TakeActionModal from '../leads/TakeActionModal';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import PrinterLoader from '../../components/PrinterLoader';

console.log("LeadProfile is:", LeadProfile);
const UserCalendar = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const currentDate = new Date();
  const [currentMonth, setCurrentMonth] = useState(new Date(currentDate.getFullYear(), currentDate.getMonth(), 1));
  const [selectedJourney, setSelectedJourney] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [filterStatus, setFilterStatus] = useState('All Statuses');
  const [filterUser, setFilterUser] = useState('All Users');
  const [isViewMode, setIsViewMode] = useState(true);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionLead, setActionLead] = useState(null);
  const [deleteLeadId, setDeleteLeadId] = useState(null);

  const confirmDelete = async () => {
    if (!deleteLeadId) return;
    try {
      await api.delete(`/leads/${deleteLeadId}`);
      toast.success("Lead deleted successfully");
      setSelectedLead(null);
      setDeleteLeadId(null);
      refreshCalendar();
    } catch (err) {
      toast.error("Failed to delete lead");
      console.error(err);
    }
  };
  
  const refreshCalendar = async () => {
    try {
      const [followupsRes, leadsRes] = await Promise.all([
        api.get('/followups'),
        api.get('/leads')
      ]);
      const fData = followupsRes.data?.data || followupsRes.data || [];
      const fullLeads = leadsRes.data?.data || leadsRes.data || [];
      const formatLead = (lead) => {
        if (!lead || !lead.companyName) return lead;
        return {
          id: lead.id,
          name: lead.companyName,
          contact: lead.contactPerson,
          phone: lead.phone,
          email: lead.email,
          contactPhone: lead.contactPhone,
          contactEmail: lead.contactEmail,
          frontCard: lead.frontCard,
          backCard: lead.backCard,
          address: lead.address,
          address2: lead.address2,
          city: lead.city,
          state: lead.state,
          pincode: lead.pincode,
          agencyType: lead.agencyType,
          machine: lead.machine?.name ? `${lead.machine.name}${lead.machineModel ? ' - ' + lead.machineModel : ''}` : 'Unknown',
          machineBrand: lead.machine?.name || '',
          machineModel: lead.machineModel || '',
          type: lead.leadType?.name ? lead.leadType.name : (lead.otherInterest ? `Other (${lead.otherInterest})` : 'Unknown'),
          assignee: lead.user?.name || 'Unassigned',
          status: lead.status?.name || 'Pending',
          source: lead.leadSource?.name || 'Unknown',
          customerType: lead.customerType?.name || null,
          createdAt: lead.createdAt,
          createdBy: lead.createdBy || 'Unknown',
          notes: lead.notes,
          followups: lead.followups || []
        };
      };
      
      let allActivities = [];
      fData.forEach(f => {
        if (f.type === 'System' || !f.date) return;
        const n = f.notes || '';
        if (n.startsWith('[Edit]') || n.startsWith('[Assigned]') || n.startsWith('[Status Change') || n.startsWith('[Closed]')) return;
        
        const assigneeId = f.lead?.user?.id || f.lead?.userId;
        const creator = usersList.find(u => u.name === f.createdBy);
        const userId = assigneeId || (creator ? creator.id : null);
        
        if (!userId) return;
        
        let status = 'Pending';
        let bgColor = '#ffedd5'; // Orange
        let textColor = '#9a3412';
        
        const leadStatus = (f.lead?.status?.name || f.lead?.status || '').toLowerCase();
        const isLeadTerminal = leadStatus.includes('complete') || leadStatus.includes('closed') || leadStatus.includes('won') || leadStatus.includes('lost');
        
        if (n.includes('[Complete') || n.includes('[Closed]') || n.includes('[Won]') || (isLeadTerminal && !n.includes('[Cancel'))) {
          status = 'Complete';
          bgColor = '#dcfce7'; // Green
          textColor = '#166534';
        } else if (n.includes('[Cancel') || n.includes('[Lost]') || leadStatus.includes('cancel')) {
          status = 'Cancelled';
          bgColor = '#f3f4f6'; // Gray
          textColor = '#4b5563';
        } else if (n.includes('[Check-In]') || n.includes('Visit Started')) {
          status = 'In Progress';
          bgColor = '#e0f2fe'; // Blue
          textColor = '#0369a1';
        } else if (new Date(f.date) < new Date()) {
          status = 'Missed';
          bgColor = '#fee2e2'; // Red
          textColor = '#991b1b';
        }
        
        allActivities.push({
          id: f.id,
          userId,
          eventDate: f.date,
          action: status,
          lead: formatLead(fullLeads.find(l => l.id === (f.lead?.id || f.leadId))) || f.lead,
          bgColor,
          textColor,
          notes: n
        });
      });
      
      allActivities.sort((a,b) => new Date(a.eventDate) - new Date(b.eventDate));
      const leadCounts = {};
      allActivities.forEach(a => {
        const lId = a.lead?.id || 'none';
        if (!leadCounts[lId]) leadCounts[lId] = 0;
        leadCounts[lId]++;
        a.visitIndex = leadCounts[lId];
      });
      
      setActivities(allActivities);
      
      // Update selected lead to trigger a refresh in AddLead modal without closing it
      if (selectedLead) {
        api.get(`/leads/${selectedLead.id}`).then(res => {
          setSelectedLead(formatLead(res.data));
        }).catch(console.error);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const monthName = currentMonth.toLocaleString('default', { month: 'long' });

  const getDaysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
  const getFirstDayOfMonth = (y, m) => new Date(y, m, 1).getDay();

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);

  const daysArray = [];
  for (let i = 0; i < firstDay; i++) {
    daysArray.push(null);
  }
  for (let i = 1; i <= daysInMonth; i++) {
    daysArray.push(i);
  }

  const [usersList, setUsersList] = useState([]);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const { default: api } = await import('../../utils/api');
        const [usersRes, followupsRes, leadsRes] = await Promise.all([
          api.get('/auth/users'),
          api.get('/followups'),
          api.get('/leads')
        ]);
        
        const displayUsers = usersRes.data.filter(u => u.status === 'Active');
        setUsersList(displayUsers.map((u, index) => {
          const colors = ['#e0e7ff', '#dcfce7', '#fecaca', '#fef3c7', '#e0f2fe'];
          const textColors = ['#3730a3', '#166534', '#991b1b', '#92400e', '#075985'];
          return {
            id: u.id,
            name: u.name,
            role: u.role,
            color: colors[index % colors.length],
            textColor: textColors[index % textColors.length]
          };
        }));
        
        let allActivities = [];
        
        const fData = followupsRes.data?.data || followupsRes.data || [];
        const fullLeads = leadsRes.data?.data || leadsRes.data || [];
        const formatLead = (lead) => {
          if (!lead || !lead.companyName) return lead; // Already formatted or invalid
          return {
            id: lead.id,
            name: lead.companyName,
            contact: lead.contactPerson,
            phone: lead.phone,
            email: lead.email,
            contactPhone: lead.contactPhone,
            contactEmail: lead.contactEmail,
            frontCard: lead.frontCard,
            backCard: lead.backCard,
            address: lead.address,
            machine: lead.machine?.name ? `${lead.machine.name}${lead.machineModel ? ' - ' + lead.machineModel : ''}` : 'Unknown',
            machineBrand: lead.machine?.name || '',
            machineModel: lead.machineModel || '',
            type: lead.leadType?.name ? lead.leadType.name : (lead.otherInterest ? `Other (${lead.otherInterest})` : 'Unknown'),
            assignee: lead.user?.name || 'Unassigned',
            status: lead.status?.name || 'Pending',
            source: lead.leadSource?.name || 'Unknown',
            customerType: lead.customerType?.name || null,
            createdAt: lead.createdAt,
            createdBy: lead.createdBy || 'Unknown',
            notes: lead.notes,
            followups: lead.followups || []
          };
        };
        
        // Group followups by lead to check terminal states
        fData.forEach(f => {
          if (f.type === 'System' || !f.date) return;
          
          const n = f.notes || '';
          // Skip intermediate or pure log events from being plotted as scheduled visits
          if (n.startsWith('[Edit]') || n.startsWith('[Assigned]') || n.startsWith('[Status Change') || n.startsWith('[Closed]')) return;
          
          const assigneeId = f.lead?.user?.id || f.lead?.userId;
          const creator = displayUsers.find(u => u.name === f.createdBy);
          const userId = assigneeId || (creator ? creator.id : null);
          
          if (!userId) return;

          let status = 'Pending';
          let bgColor = '#ffedd5'; // Orange
          let textColor = '#9a3412';
          
          const leadStatus = (f.lead?.status?.name || f.lead?.status || '').toLowerCase();
          const isLeadTerminal = leadStatus.includes('complete') || leadStatus.includes('closed') || leadStatus.includes('won') || leadStatus.includes('lost');
          
          if (n.includes('[Complete') || n.includes('[Closed]') || n.includes('[Won]') || (isLeadTerminal && !n.includes('[Cancel'))) {
            status = 'Complete';
            bgColor = '#dcfce7'; // Green
            textColor = '#166534';
          } else if (n.includes('[Cancel') || n.includes('[Lost]') || leadStatus.includes('cancel')) {
            status = 'Cancelled';
            bgColor = '#f3f4f6'; // Gray
            textColor = '#4b5563';
          } else if (n.includes('[Check-In]') || n.includes('Visit Started')) {
            status = 'In Progress';
            bgColor = '#e0f2fe'; // Blue
            textColor = '#0369a1';
          } else if (new Date(f.date) < new Date()) {
            status = 'Missed';
            bgColor = '#fee2e2'; // Red
            textColor = '#991b1b';
          }
          allActivities.push({
            id: f.id,
            userId,
            eventDate: f.date,
            action: status,
            lead: formatLead(fullLeads.find(l => l.id === (f.lead?.id || f.leadId))) || f.lead,
            bgColor,
            textColor,
            notes: n
          });
        });
        
        allActivities.sort((a,b) => new Date(a.eventDate) - new Date(b.eventDate));
        const leadCounts = {};
        allActivities.forEach(a => {
          const lId = a.lead?.id || 'none';
          if (!leadCounts[lId]) leadCounts[lId] = 0;
          leadCounts[lId]++;
          a.visitIndex = leadCounts[lId];
        });
        
        setActivities(allActivities);
      } catch (err) {
        console.error('Failed to fetch calendar data', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getActivityForDay = (day) => {
    const dayActivities = activities.filter(a => {
      const d = new Date(a.eventDate);
      return d.getDate() === day && d.getMonth() === month && d.getFullYear() === year;
    }).filter(a => {
      if (filterStatus !== 'All Statuses' && a.action !== filterStatus) return false;
      if (filterUser !== 'All Users' && a.userId !== filterUser) return false;
      return true;
    });

    const activeBadges = [];
    dayActivities.forEach(a => {
       const u = usersList.find(usr => usr.id === a.userId);
       if (!u) return;
       
       if (user?.role !== 'Admin' && u.id !== user?.id) return;
       
       const leadName = a.lead ? (a.lead.companyName || a.lead.name || 'Unknown Lead') : null;
       const badgeKey = `${a.id}`;
       
       if (!activeBadges.find(b => b.key === badgeKey)) {
         activeBadges.push({
           key: badgeKey,
           user: u,
           lead: a.lead,
           displayName: leadName ? `${leadName} (${u.name})` : u.name,
           color: a.bgColor,
           textColor: a.textColor
         });
       }
    });
    return activeBadges;
  };

  const getJourneyLogs = (userId, day) => {
    const dayActivities = activities.filter(a => {
      const d = new Date(a.eventDate);
      return d.getDate() === day && d.getMonth() === month && d.getFullYear() === year && a.userId === userId;
    });

    return dayActivities
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .map(a => ({
        time: new Date(a.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        action: a.action,
        icon: a.iconType === 'Call' ? <PhoneCall size={16} color="var(--color-primary)" /> : 
              a.iconType === 'Lead' ? <CheckCircle size={16} color="var(--color-success)" /> : 
              <CheckCircle size={16} color="var(--color-info)" />
      }));
  };

  const handleUserClick = (e, user, day) => {
    e.stopPropagation();
    setSelectedJourney({
      user,
      day,
      dateString: `${monthName} ${day}, ${year}`,
      logs: getJourneyLogs(user.id, day)
    });
  };

  return (
    <div>
      {loading && <PrinterLoader />}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">User Calendar</h2>
          <p className="text-muted mt-2">View monthly active users and their daily journey.</p>
        </div>
        
        <div className="flex gap-4 items-center">
          <select className="input-field" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--color-border)', backgroundColor: 'white' }}>
            <option value="All Statuses">All Statuses</option>
            <option value="Pending">Pending</option>
            <option value="Complete">Complete</option>
            <option value="Missed">Missed</option>
            <option value="In Progress">In Progress</option>
            <option value="Cancelled">Cancelled</option>
          </select>
          <select className="input-field" value={filterUser} onChange={e => setFilterUser(e.target.value)} style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--color-border)', backgroundColor: 'white' }}>
            <option value="All Users">All Users</option>
            {usersList.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <div className="flex gap-4 items-center bg-white px-3 py-1.5 rounded-md border" style={{ borderColor: 'var(--color-border)' }}>
          <button className="btn p-1 hover:bg-slate-100 rounded" onClick={prevMonth} style={{ border: 'none', background: 'transparent' }}><ChevronLeft size={20} /></button>
          <span className="font-semibold px-4 w-32 text-center">{monthName} {year}</span>
          <button className="btn p-1 hover:bg-slate-100 rounded" onClick={nextMonth} style={{ border: 'none', background: 'transparent' }}><ChevronRight size={20} /></button>
        </div>
      </div>
      </div>

      <div className="card" style={{ padding: '0', overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', backgroundColor: 'var(--color-bg)', borderBottom: '1px solid var(--color-border)' }}>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(dayName => (
            <div key={dayName} className="font-bold text-center py-3 text-sm text-muted uppercase tracking-wider border-r last:border-r-0" style={{ borderColor: 'var(--color-border)' }}>
              {dayName}
            </div>
          ))}
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', backgroundColor: 'var(--color-border)', gap: '1px' }}>
          {daysArray.map((day, index) => {
            if (!day) return <div key={`empty-${index}`} style={{ backgroundColor: 'white', minHeight: '140px' }} />;
            
            const activeUsers = getActivityForDay(day);
            const isToday = day === currentDate.getDate() && month === currentDate.getMonth() && year === currentDate.getFullYear();
            
            return (
              <div key={day} style={{ backgroundColor: isToday ? '#f8fafc' : 'white', minHeight: '140px', padding: '0.75rem' }} className="relative group hover:bg-slate-50 transition-colors">
                <span className={`inline-flex items-center justify-center font-bold text-sm mb-2 ${isToday ? 'bg-primary text-white w-7 h-7 rounded-full' : 'text-slate-700'}`}>
                  {day}
                </span>
                
                <div className="flex flex-col gap-1.5 mt-1">
                  {activeUsers.map((badge, idx) => (
                    <div 
                      key={idx}
                      onClick={(e) => {
                         e.stopPropagation();
                         if (badge.lead) {
                            setIsViewMode(true); setSelectedLead(badge.lead);
                         } else {
                            handleUserClick(e, badge.user, day);
                         }
                      }}
                      className="text-xs font-semibold px-2 py-1 rounded cursor-pointer truncate transition-opacity hover:opacity-80 flex items-center justify-between"
                      style={{ backgroundColor: badge.color, color: badge.textColor, border: `1px solid ${badge.textColor}30` }}
                      title={badge.lead ? `View lead ${badge.lead.companyName}` : `View ${badge.user.name}'s journey`}
                    >
                      <span className="truncate">{badge.displayName}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {selectedLead && (
        <div className="modal-overlay" style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)',
          display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 50, padding: '2rem'
        }}>
          <div className="hide-scrollbar" style={{
            backgroundColor: 'var(--color-bg-card)', width: '100%', maxWidth: '1200px',
            maxHeight: '90vh', overflowY: 'auto', borderRadius: '24px',
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', display: 'flex',
            flexDirection: 'column', position: 'relative'
          }}>
            <AddLead 
              onClose={() => setSelectedLead(null)} 
              initialData={selectedLead} 
              isViewMode={isViewMode} 
              onEdit={(lead) => { setSelectedLead(null); setTimeout(() => { setSelectedLead(lead); setIsViewMode(false); }, 0); }}
              onDelete={(id) => setDeleteLeadId(id)}
              onAction={(lead) => {
                setActionLead(lead);
                setShowActionModal(true);
              }}
            />
          </div>
        </div>
      )}
      
      {showActionModal && actionLead && (
        <TakeActionModal 
          lead={actionLead} 
          onClose={() => setShowActionModal(false)}
          onSuccess={() => {
            setShowActionModal(false);
            refreshCalendar();
          }} 
        />
      )}

      {selectedJourney && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)',
          display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 100, padding: '1rem'
        }}>
          <div style={{
            backgroundColor: 'var(--color-bg-card)', padding: '2rem', borderRadius: 'var(--border-radius-lg)',
            width: '100%', maxWidth: '600px', boxShadow: 'var(--shadow-xl)', maxHeight: '90vh', overflowY: 'auto'
          }}>
            <div className="flex justify-between items-start mb-6 pb-4 border-b" style={{ borderColor: 'var(--color-border)' }}>
              <div className="flex items-center gap-4">
                <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: selectedJourney.user.color, color: selectedJourney.user.textColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.25rem', fontWeight: 'bold' }}>
                  {selectedJourney.user.name.charAt(0)}
                </div>
                <div>
                  <h3 className="text-xl font-bold">{selectedJourney.user.name}</h3>
                  <span className="text-sm font-semibold" style={{ color: selectedJourney.user.textColor }}>{selectedJourney.user.role}</span>
                  <div className="text-sm text-muted mt-0.5">Activity Journey for {selectedJourney.dateString}</div>
                </div>
              </div>
              <button onClick={() => setSelectedJourney(null)} className="btn hover:bg-slate-100 rounded-full p-2" style={{ border: 'none', background: 'transparent' }}>
                <X size={24} className="text-muted" />
              </button>
            </div>
            
            <div className="flex-col gap-0 relative" style={{ paddingLeft: '1.5rem', marginTop: '1rem' }}>
              <div style={{ position: 'absolute', left: '11px', top: '20px', bottom: '20px', width: '2px', backgroundColor: 'var(--color-border)', zIndex: 0 }}></div>
              
              {selectedJourney.logs.map((log, i) => (
                <div key={i} className="flex gap-4 items-start relative z-10" style={{ paddingBottom: i === selectedJourney.logs.length - 1 ? '0' : '2rem' }}>
                  <div style={{ 
                    marginTop: '2px', width: '24px', height: '24px', borderRadius: '50%', backgroundColor: 'white', 
                    border: '2px solid var(--color-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginLeft: '-22px'
                  }}>
                    {log.icon}
                  </div>
                  <div>
                    <p className="font-semibold text-main">{log.action}</p>
                    <span className="text-sm text-muted font-medium">{log.time}</span>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-8 flex justify-end">
              <button className="btn" onClick={() => setSelectedJourney(null)} style={{ border: '1px solid var(--color-border)', backgroundColor: 'white' }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {deleteLeadId && (
        <div className="modal-backdrop">
          <div className="modal-content fade-in" style={{ maxWidth: '400px', padding: '2rem', textAlign: 'center' }}>
            <div style={{ width: '4rem', height: '4rem', borderRadius: '50%', backgroundColor: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem auto' }}>
              <Trash2 size={32} className="text-danger" />
            </div>
            <h3 className="text-xl font-bold mb-3 text-main">Delete Lead</h3>
            <p className="text-muted mb-8 text-sm">Are you sure you want to delete this lead? This action cannot be undone and all associated follow-ups will be permanently removed.</p>
            <div className="flex gap-4 justify-center">
              <button className="btn btn-secondary flex-1" onClick={() => setDeleteLeadId(null)}>
                Cancel
              </button>
              <button className="btn flex-1" onClick={confirmDelete} style={{ backgroundColor: 'var(--color-danger)', color: 'white' }}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserCalendar;
